/**
 * The game-creation DECISION REGISTRY — the single source of truth for the
 * PLATFORM PRIMITIVES a creation locks in: who plays (play modes), input
 * hardware coverage, palette, world shape. The wizard's review step, the
 * inferred {@link import('./types.js').GameSettings} type, validation, the
 * prompt compiler, and the builder's settings gate ALL derive from this one
 * table (one-path rule: no second copy of any option list may exist — the
 * portal and functions import from here).
 *
 * Since v4 (concept-first creation), the registry deliberately does NOT
 * describe game MECHANICS — the GameDesign doc (design/types.ts) is the
 * mechanics authority. Only decisions the PLATFORM enforces (seat shapes,
 * hosting, input coverage, render-manifest palette/aspect) live here.
 *
 * DESIGN CONSTRAINTS (load-bearing — see spec tests):
 *   - The registry is PURE SERIALIZABLE DATA: no functions/closures. Logic
 *     (visibility evaluation, defaults, compilation) lives in the sibling
 *     modules so the table stays snapshot-testable and the inferred types
 *     stay free of circular references.
 *   - Decision ids and option ids are STABLE within a schema epoch (stored
 *     creator answers are keyed by them; FHIR Questionnaire discipline).
 *     Evolution is ADDITIVE: new decisions/options bump
 *     {@link SETTINGS_SCHEMA_VERSION}. v4 was a deliberate EPOCH BREAK — see
 *     the version note; pre-v4 answer sets simply read as unanswered.
 *   - `visibleWhen` conditions may only reference decisions that THEMSELVES
 *     have no `visibleWhen` (single-pass completion: the pass evaluates
 *     conditions once against the fallback-overlaid map, so chained
 *     visibility would be order-dependent; the registry test enforces this).
 */
import type { CreationStyle, TemplateFamily } from '../types.js';

/** Version stamped on every stored answer set. Bump on ANY registry change.
 *  v2: Phase-2 unlocked the `onlineVersus` player mode for paddle (additive).
 *  v3: strengthened the `inputScheme` spec fragments for mobile touch
 *  (additive).
 *  v4: the concept-first EPOCH BREAK — the genre axis and every mechanics
 *  decision (genre, playerMode, sessionShape, targetScore, objective,
 *  scoring, difficulty, progression, pace, theme, juice, audio) are DELETED;
 *  the GameDesign doc owns mechanics now. `playModes` (multi) replaces
 *  `playerMode`. There is deliberately NO migration: a v3 answer set reads
 *  as UNANSWERED under v4 and its creation builds spec-less until the
 *  creator re-answers (the honest interim until the A5 wipe).
 *  v5: the RUNTIME controls section (additive) — `touchScheme` + its per-
 *  scheme params, mirroring the `@djgg/controls` catalog (the invariant test
 *  locks them together), plus the non-decision `keyBindings` extra. Runtime
 *  decisions apply LIVE via the /play proxy injection, not via builds.
 *  v6: `zones-lr.dragAxis` now fails open to two-axis movement (`both`), so
 *  generated games cannot silently lose horizontal steering.
 *  v7: the portrait-first REFRAME (copy + section only — no id, no spec
 *  fragment, and no fallback changed). `orientation` asks "Where will people
 *  play this?" in PLAYER terms and moves from the `theme` section to the
 *  spine `players` section: on phones the world shape decides whether a game
 *  is playable at all, so it is a spine decision, not a visual-identity one.
 *  Its `fallback` deliberately STAYED `'landscape'` in the reframe change —
 *  flipping it silently reshapes any existing creation whose sparse settings
 *  never answered the decision. The flip to `'portrait'` shipped in its own
 *  change once the corpus was VERIFIED to carry explicit answers: every
 *  answered creation stores `orientation` explicitly (the wizard has always
 *  seeded answers dense), so the `orientation-landscape` backfill
 *  (tools/ops/lib/orientationBackfill.mjs) plans zero rows and the fallback
 *  only ever reaches settings sets born after the flip.
 *  The same backfill is what keeps the section move safe: an un-backfilled
 *  creation with v4 answers now reads as missing a SPINE decision.
 *  v8: the `zone-fire` touch scheme (additive) — a new `touchScheme` OPTION
 *  plus its three params (`moveControl`, `actionExtent`, `holdDelay`). Purely
 *  additive: no existing id, fallback, or spec fragment changed, so every
 *  creation's compiled spec is byte-identical to what v7 compiled (locked by
 *  the "additive only" case in test/spec.compile.test.ts). The bump exists
 *  because the answerable set grew, not because anything moved.
 *  v9: `dpadStyle` (previously `dpad-buttons`-only) is now ALSO answerable
 *  under `zone-fire` — a one-axis zone-fire game (moveControl: 'dpad') can
 *  drop the dead up/down arrows the same way `dpad-buttons` already could.
 *  No id, option, fallback, or spec TEXT changed — `dpadStyle` reuses its
 *  existing two options verbatim. `visibleWhen` can only condition on an
 *  UNCONDITIONED decision (the registry's single-pass rule; enforced by the
 *  referential-integrity test), and `moveControl` itself carries a
 *  `visibleWhen`, so `dpadStyle` cannot be gated on "zone-fire AND
 *  moveControl=dpad" — only on `touchScheme`. It is gated on `touchScheme in
 *  ['dpad-buttons', 'zone-fire']` instead: visible whenever EITHER scheme is
 *  chosen, same as `actionExtent`/`holdDelay` are already visible for the
 *  whole zone-fire scheme regardless of `moveControl`. The renderer still
 *  only ever reads it when `moveControl: 'dpad'` (`schemeToSpec`), so an
 *  answered-but-irrelevant value (zone-fire + a joystick) has zero runtime
 *  effect — it only newly reaches the COMPILED PROMPT for the zone-fire
 *  scheme (see the v9 case in test/spec.compile.test.ts, which also defends
 *  why that prompt change didn't need eval spend).
 *  v10: additive gesture-first touch profiles: tap-target, hold-target,
 *  relative-steer, and gutter-dpad.
 *  v11: additive tap-sides profile for discrete left/right actions around a
 *  controlled character (for example lander attitude), without a D-pad.
 *  v12: additive tap-fire profile for buffered front-view shooting-gallery
 *  input. Existing decisions and compiled fragments are unchanged.
 */
export const SETTINGS_SCHEMA_VERSION = 12;

/**
 * Wizard sections in presentation order. `spine: true` sections are
 * MANDATORY (every visible decision must be answered before the first
 * build); the rest are pre-filled from registry fallbacks and skippable.
 */
export const SECTIONS = [
  { id: 'players', label: 'Players & controls', spine: true },
  { id: 'controls', label: 'Touch & keys', spine: false },
  { id: 'theme', label: 'Theme & visual identity', spine: false },
] as const;

/** A wizard section id. */
export type SectionId = (typeof SECTIONS)[number]['id'];

/**
 * Data-only visibility condition (FHIR `enableWhen` style). A decision with
 * `visibleWhen` is shown only when EVERY condition holds (AND). `includes`
 * targets `multi` decisions (set membership).
 */
export interface Condition {
  /** The referenced decision id (spine-section decisions only). */
  readonly decision: string;
  readonly op: 'eq' | 'in' | 'includes';
  readonly value: string | readonly string[];
}

/** One selectable option of a {@link ChoiceDecision} / {@link MultiChoiceDecision}. */
export interface ChoiceOption {
  /** Stable id — never renamed (stored answers are keyed by it). */
  readonly id: string;
  readonly label: string;
  /** Optional one-line explanation shown under the label. */
  readonly blurb?: string;
  /** Deterministic prompt fragment the compiler emits when this option is
   *  chosen. Empty for options that cannot yet be built (locked). */
  readonly spec: string;
  /** Rendered but not selectable — a "coming soon" capability the platform
   *  cannot build yet (validation rejects it). Unlocking is an additive flip. */
  readonly locked?: boolean;
}

interface DecisionBase {
  readonly label: string;
  readonly section: SectionId;
  /** Optional help text shown beside the control. */
  readonly help?: string;
  readonly visibleWhen?: readonly Condition[];
  /** RUNTIME decision: applied live to the published game (the /play proxy
   *  injects it; the shell reads it at boot) — never part of the pending
   *  "applies on next build" story. The studio renders these with
   *  applies-immediately copy and the iterate banner ignores them. */
  readonly runtime?: boolean;
}

/** A pick-one decision. */
export interface ChoiceDecision extends DecisionBase {
  readonly kind: 'choice';
  readonly options: readonly ChoiceOption[];
  /** Static fallback default. */
  readonly fallback: string;
}

/** A pick-MANY decision — the stored value is a SET of option ids (order
 *  irrelevant; comparison and display are set-shaped). */
export interface MultiChoiceDecision extends DecisionBase {
  readonly kind: 'multi';
  readonly options: readonly ChoiceOption[];
  /** Static fallback default selection (non-empty). */
  readonly fallback: readonly string[];
}

/** One preset of a {@link PaletteDecision}. */
export interface PalettePreset {
  /** Stable id — never renamed. */
  readonly id: string;
  readonly label: string;
  /** Ordered colors, `#rrggbb` lowercase (the render-manifest contract —
   *  see renderManifest.ts HEX_COLOR). The settings gate asserts the built
   *  manifest's palette contains every one of these. */
  readonly colors: readonly string[];
  /** Deterministic prompt fragment describing how to use the palette. */
  readonly spec: string;
}

/** A pick-one color-palette decision (feeds render-manifest.json). */
export interface PaletteDecision extends DecisionBase {
  readonly kind: 'palette';
  readonly presets: readonly PalettePreset[];
  /** Preset id used as the static fallback. */
  readonly fallback: string;
}

/** Any decision the registry can declare. */
export type AnyDecision = ChoiceDecision | MultiChoiceDecision | PaletteDecision;

/**
 * THE decision table. Iteration order within a section is presentation order
 * (sections order via {@link SECTIONS}).
 */
export const DECISIONS = {
  // ── players & controls (spine) ──────────────────────────────────────────
  playModes: {
    kind: 'multi',
    section: 'players',
    label: 'Play modes',
    help: 'Every way this game can be played — pick all that apply.',
    options: [
      {
        id: 'solo',
        label: 'Solo',
        blurb: 'One player against the game.',
        spec: 'Solo mode: a single human seat plays alone against the game.',
      },
      {
        id: 'vsBot',
        label: 'You vs the computer',
        blurb: 'One human seat; a CPU bot fills the other.',
        spec: 'Versus-computer mode: one human seat with a CPU bot filling the second seat.',
      },
      {
        id: 'localVersus',
        label: 'Two players, one screen',
        blurb: 'Shared keyboard/screen versus.',
        spec: 'Local versus: two human seats share the screen and keyboard (separate key clusters per player).',
      },
      {
        id: 'onlineVersus',
        label: 'Online multiplayer',
        blurb: 'Play against another person over the network.',
        spec: 'Online head-to-head: two human seats over the network, server-authoritative.',
      },
    ],
    fallback: ['solo'],
  },
  botSkill: {
    kind: 'choice',
    section: 'players',
    label: 'Computer skill',
    visibleWhen: [{ decision: 'playModes', op: 'includes', value: 'vsBot' }],
    options: [
      {
        id: 'casual',
        label: 'Casual',
        spec: 'The CPU bot plays casually — human-paced, imperfect, and beatable by a first-time player.',
      },
      {
        id: 'adaptive',
        label: 'Adaptive',
        spec: 'The CPU bot adapts gently to the player (use the bot tier ladder without obvious rubber-banding or hidden information).',
      },
    ],
    fallback: 'adaptive',
  },
  inputScheme: {
    kind: 'choice',
    section: 'players',
    label: 'Controls',
    options: [
      {
        id: 'keyboard',
        label: 'Keyboard',
        spec: 'Controls: keyboard (arrow keys / WASD) on desktop — map the semantic key state (InputFrame.keys) to commands in src/adapter.ts input(). Because most players are on a PHONE, ALSO declare the adapter’s `touch` layout (a joystick and/or on-screen buttons — the platform renders them and folds them into the same semantic keys) so the game is fully playable one-handed on a phone.',
      },
      {
        id: 'pointer',
        label: 'Mouse / touch',
        spec: 'Controls: pointer/touch only. Read the InputFrame pointer (canvas + world coordinates, down state) in src/adapter.ts input() so BOTH mouse AND finger touch work — never mouse-only. Every interaction must be reachable by tapping/dragging the play field one-handed on a phone.',
      },
      {
        id: 'both',
        label: 'Keyboard + touch',
        spec: 'Controls: BOTH keyboard AND pointer/touch must work — desktop keyboard+mouse, and finger touch on a phone. Map the semantic keys AND the pointer in src/adapter.ts input(), and declare the adapter’s `touch` layout (joystick/buttons) so the game is fully playable one-handed on a phone.',
      },
    ],
    fallback: 'keyboard',
  },

  // ── touch & keys (runtime — applied live, no rebuild) ───────────────────
  // Mirrors the `@djgg/controls` catalog: option ids = ControlSchemeId (+
  // `auto`), param decisions = the catalog's SchemeParamDefs (same keys, same
  // option ids). The spec.controls invariant test locks the two together.
  touchScheme: {
    kind: 'choice',
    section: 'controls',
    label: 'Touch controls',
    help: 'How the game plays on phones. Applies on the next game load — no rebuild.',
    runtime: true,
    options: [
      {
        id: 'auto',
        label: 'Automatic',
        blurb: 'The game design picks the layout.',
        spec: 'Touch controls: pick the layout that fits the mechanics and declare it as the adapter\u2019s `touch` layout (stick/buttons + a recommended `scheme` preset) — the platform renders it and folds it into the same semantic input frame.',
      },
      {
        id: 'stick-buttons',
        label: 'Joystick + buttons',
        blurb: 'A movement joystick on the left, action buttons on the right.',
        spec: 'Touch controls: the platform renders a movement JOYSTICK (feeding the semantic directional keys + the analog InputFrame.stick) plus your declared action buttons — read the semantic keys/stick in input().',
      },
      {
        id: 'dpad-buttons',
        label: 'D-pad + buttons',
        blurb: 'Direction pad on the left, action buttons on the right.',
        spec: 'Touch controls: the platform renders a fixed D-PAD feeding the semantic directional keys, plus your declared action buttons — read the semantic keys in input().',
      },
      {
        id: 'zones-lr',
        label: 'Drag & tap zones',
        blurb: 'Drag the left half to steer, tap the right half to act.',
        spec: 'Touch controls: the platform splits the screen — dragging the LEFT half drives the analog stick/directional keys, tapping the RIGHT half holds the primary action — read the semantic keys/stick in input().',
      },
      {
        id: 'zone-fire',
        label: 'Fire anywhere',
        blurb:
          'One anchored movement control; the rest of the screen fires. Hold for the second action.',
        spec: 'Touch controls: the platform renders ONE anchored movement control bottom-left (feeding the semantic directional keys + the analog InputFrame.stick) and turns the REST of the screen into the action surface — a touch anywhere on it holds your FIRST declared action button for as long as it lasts, and once it outlasts the long-press threshold it additionally holds your SECOND one. Declare both in the adapter’s `touch.buttons` (the semantic `action` id is always the first), and read the semantic keys/stick plus InputFrame.held[<buttonId>] in input(). Declare only one button and the long press simply keeps holding it.',
      },
      {
        id: 'drag-track',
        label: 'Drag to move',
        blurb: 'Your piece follows your finger anywhere on the screen.',
        spec: 'Touch controls: no overlay chrome — the finger drags directly on the play field; read InputFrame.pointer (canvas + world coordinates) in input().',
      },
      {
        id: 'tap-play',
        label: 'Tap the board',
        blurb: 'Play by tapping and dragging pieces directly.',
        spec: 'Touch controls: no overlay — the player taps/drags the play field directly; read InputFrame.pointer (canvas + world coordinates) in input().',
      },
      {
        id: 'tap-fire',
        label: 'Tap to fire',
        blurb: 'Tap a target to fire; hold for an automatic weapon.',
        spec: 'Touch controls: consume buffered playfield gestures. A quick `tap` fires once at its world coordinates; `hold-start`/`hold-end` drive a sustained trigger for automatic weapons; target and drag coordinates aim the reticle, and dragging cancels a pending tap. Render no joystick or action-button chrome.',
      },
      {
        id: 'tap-target',
        label: 'Tap to move',
        blurb: 'Tap the playfield to move there; tap again for the action.',
        spec: 'Touch controls: read the shell gesture frame in input() and map tap targets to movement. Use the second tap gesture only for a distinct secondary action; do not delay the first tap.',
      },
      {
        id: 'hold-target',
        label: 'Hold to move',
        blurb: 'Hold or drag toward a point to steer there; tap to act.',
        spec: 'Touch controls: read a held target gesture in input() and steer continuously toward its world point. A quick tap remains available for the primary action.',
      },
      {
        id: 'relative-steer',
        label: 'Touch-relative steering',
        blurb: 'Touch around your character to steer relative to its position.',
        spec: 'Touch controls: read a relative gesture in input() and compare its world point to the controlled character position. Keep the character visible; a tap remains available for the primary action.',
      },
      {
        id: 'tap-sides',
        label: 'Tap either side',
        blurb: 'Tap left or right of your craft for one discrete turn.',
        spec: 'Touch controls: consume only brief tap gestures for movement. Compare each tap worldX with the controlled character worldX and emit exactly one left or right action; never retain a target, continuously steer, or react to drag/hold gestures. Keep explicit actions on the declared buttons.',
      },
      {
        id: 'gutter-dpad',
        label: 'Edge movement',
        blurb: 'Movement stays in the screen edge, keeping the playfield clear.',
        spec: 'Touch controls: render movement in the safe screen gutter so the playfield stays visible. Map its directional keys in input(); use the action buttons for explicit actions.',
      },
    ],
    fallback: 'auto',
  },
  stickMode: {
    kind: 'choice',
    section: 'controls',
    label: 'Joystick placement',
    visibleWhen: [{ decision: 'touchScheme', op: 'eq', value: 'stick-buttons' }],
    runtime: true,
    options: [
      { id: 'dynamic', label: 'Appears under your finger', spec: 'Joystick placement: relative — it springs up under the finger.' },
      { id: 'static', label: 'Fixed in the corner', spec: 'Joystick placement: fixed bottom-left.' },
    ],
    fallback: 'dynamic',
  },
  dpadStyle: {
    kind: 'choice',
    section: 'controls',
    label: 'Pad style',
    // Answerable under BOTH schemes that can render a d-pad — see the v9
    // registry history note above for why this can't also require
    // moveControl==='dpad' under zone-fire.
    visibleWhen: [{ decision: 'touchScheme', op: 'in', value: ['dpad-buttons', 'zone-fire', 'gutter-dpad'] }],
    runtime: true,
    options: [
      { id: '4dir', label: '4 directions', spec: 'D-pad style: four directions (diagonals hold two keys).' },
      { id: 'horizontal', label: 'Left / right only', spec: 'D-pad style: left/right only — vertical actions come from buttons.' },
    ],
    fallback: '4dir',
  },
  dragAxis: {
    kind: 'choice',
    section: 'controls',
    label: 'Drag direction',
    visibleWhen: [{ decision: 'touchScheme', op: 'eq', value: 'zones-lr' }],
    runtime: true,
    options: [
      { id: 'y', label: 'Up / down', spec: 'Drag zone axis: vertical (stick.y / the up-down keys).' },
      { id: 'x', label: 'Left / right', spec: 'Drag zone axis: horizontal (stick.x / the left-right keys).' },
      { id: 'both', label: 'Any direction', spec: 'Drag zone axis: free — both stick axes / all four directional keys.' },
    ],
    fallback: 'both',
  },
  moveControl: {
    kind: 'choice',
    section: 'controls',
    label: 'Movement control',
    visibleWhen: [{ decision: 'touchScheme', op: 'eq', value: 'zone-fire' }],
    runtime: true,
    options: [
      { id: 'stick', label: 'Joystick', spec: 'Movement control: an anchored joystick, fixed bottom-left.' },
      { id: 'dpad', label: 'D-pad', spec: 'Movement control: a fixed D-pad, bottom-left.' },
    ],
    fallback: 'stick',
  },
  actionExtent: {
    kind: 'choice',
    section: 'controls',
    label: 'Action area',
    visibleWhen: [{ decision: 'touchScheme', op: 'eq', value: 'zone-fire' }],
    runtime: true,
    options: [
      { id: 'full', label: 'Everywhere else', spec: 'Action surface: the whole screen outside the movement control.' },
      { id: 'right', label: 'The right half', spec: 'Action surface: the right half of the screen.' },
    ],
    fallback: 'full',
  },
  holdDelay: {
    kind: 'choice',
    section: 'controls',
    label: 'Long-press time',
    visibleWhen: [{ decision: 'touchScheme', op: 'eq', value: 'zone-fire' }],
    runtime: true,
    options: [
      { id: '250', label: 'Quick (0.25s)', spec: 'Long-press threshold: 0.25s.' },
      { id: '350', label: 'Standard (0.35s)', spec: 'Long-press threshold: 0.35s.' },
      { id: '500', label: 'Deliberate (0.5s)', spec: 'Long-press threshold: 0.5s.' },
    ],
    fallback: '350',
  },

  // ── theme & visual identity (advanced) ──────────────────────────────────
  palette: {
    kind: 'palette',
    section: 'theme',
    label: 'Color palette',
    presets: [
      {
        id: 'neonNight',
        label: 'Neon night',
        colors: ['#0b0f1a', '#00e5ff', '#ff2d78', '#f4f7ff'],
        spec: 'Palette: near-black navy field; cyan and hot-pink actors; near-white accents.',
      },
      {
        id: 'retroArcade',
        label: 'Retro arcade',
        colors: ['#1a1423', '#ffb703', '#e63946', '#f1faee'],
        spec: 'Palette: dark plum field; amber and signal-red actors; off-white accents.',
      },
      {
        id: 'minimalMono',
        label: 'Minimal mono',
        colors: ['#111111', '#e0e0e0', '#888888', '#ffffff'],
        spec: 'Palette: near-black field; light-grey actors; mid-grey secondary; white accents.',
      },
      {
        id: 'deepSpace',
        label: 'Deep space',
        colors: ['#050510', '#7b2cbf', '#00b4d8', '#e0aaff'],
        spec: 'Palette: void-black field; violet and cyan actors; pale-lavender accents.',
      },
      {
        id: 'forestDawn',
        label: 'Forest dawn',
        colors: ['#12231a', '#52b788', '#ffd166', '#f0fff1'],
        spec: 'Palette: deep-green field; leaf-green and warm-gold actors; mint-white accents.',
      },
      {
        id: 'candyPop',
        label: 'Candy pop',
        colors: ['#2b1b2f', '#ff6fb5', '#7ae582', '#fff3b0'],
        spec: 'Palette: dark-berry field; bubblegum-pink and lime actors; cream accents.',
      },
    ],
    fallback: 'neonNight',
  },
  // `orientation` belongs to the SPINE `players` section (v7), but its KEY
  // stays here, last: DECISION_IDS is `Object.keys(DECISIONS)` and that order
  // is compileSpec's line order, which must not churn (byte-identical spec
  // per settings is what keeps repair rounds and evals stable). Section
  // drives PRESENTATION (wizard review, SettingsPanel tabs); key order drives
  // the PROMPT. The two are deliberately independent.
  orientation: {
    kind: 'choice',
    section: 'players',
    label: 'Where will people play this?',
    help: 'The world shape is the canvas shape — a phone held upright is tall, a desktop screen is wide.',
    options: [
      {
        id: 'landscape',
        label: 'Desktops first — wide screen',
        spec: 'World: landscape, wider than tall (roughly 16:9 world units).',
      },
      {
        id: 'portrait',
        label: 'Phones first — tall screen',
        spec: 'World: portrait, taller than wide (roughly 9:16 world units).',
      },
      {
        id: 'square',
        label: 'Both — square world',
        spec: 'World: square, equal width and height.',
      },
    ],
    fallback: 'portrait',
  },
} as const satisfies Record<string, AnyDecision>;

/** A play-mode option id (the `playModes` decision's option union). */
export type PlayModeId = (typeof DECISIONS)['playModes']['options'][number]['id'];

/** The seat shape ONE play mode implies — gate-verified against the generated
 *  GameDescriptor and consumed by the Phase-2 online host. */
export interface SeatShape {
  readonly minPlayers: number;
  readonly maxPlayers: number;
  /** True when the generated game MUST implement `createBot`. */
  readonly bots: boolean;
}

/**
 * Play mode → seat shape. The ONLY seat derivation on the platform:
 * {@link import('./compile.js').expectedInvariants} folds the SELECTED modes'
 * shapes into the descriptor bounds the settings gate asserts.
 */
export const MODE_SEATS: Readonly<Record<PlayModeId, SeatShape>> = {
  solo: { minPlayers: 1, maxPlayers: 1, bots: false },
  vsBot: { minPlayers: 2, maxPlayers: 2, bots: true },
  localVersus: { minPlayers: 2, maxPlayers: 2, bots: false },
  onlineVersus: { minPlayers: 2, maxPlayers: 2, bots: false },
};

/** All gallery styles (single source; the portal picker and the functions'
 *  validation both import this — do not re-declare the list). */
export const CREATION_STYLES: readonly CreationStyle[] = [
  'rpg',
  'action',
  'shooter',
  'sidescroller',
  'puzzle',
  'strategy',
  'word',
];

/** Human-readable gallery-style labels. Keep the ids in {@link CREATION_STYLES}
 * stable: they are stored on creations and are the filter/API vocabulary. */
export const CREATION_STYLE_LABELS: Readonly<Record<CreationStyle, string>> = {
  rpg: 'rpg',
  action: 'action',
  shooter: 'shooter',
  sidescroller: 'sidescroller',
  puzzle: 'puzzle',
  strategy: 'strategy',
  word: 'Word Games',
};

/** The buildable starter families — every creation seeds from one. Excludes
 *  `'native'` (platform games never flow through the builder or the settings
 *  system; see {@link import('../types.js').Creation} `kind`). */
export type BuildableTemplate = Exclude<TemplateFamily, 'native'>;

/** All buildable starter template families (single source, same rule as
 *  {@link CREATION_STYLES}). The concept-analysis pass hints one of these
 *  (`engineHint`); `'neutral'` is the mechanics-agnostic family concept-first
 *  creation seeds from, carrying the least starter gravity (a create build is
 *  expected to replace its gameplay wholesale). Buildable-only — never
 *  `'native'`. */
export const TEMPLATE_FAMILY_IDS: readonly BuildableTemplate[] = [
  'paddle',
  'shooter',
  'gallery-shooter',
  'puzzle',
  'blocks',
  'sidescroller',
  'wordplay',
  'service',
  'duel',
  'neutral',
];

/**
 * Families that once existed and were RETIRED (2026-09-03: platformer — the
 * rooms experiment, archived on `archive/platformer-rooms`). Stored creation
 * docs keep the family id and their published bundles stay playable, but the
 * platform accepts no new creates, builds, or forks for them — enforced at
 * the create/build/fork boundary with a clear message, nowhere else.
 */
export const RETIRED_TEMPLATE_FAMILIES: ReadonlySet<TemplateFamily> = new Set(['platformer']);
